import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { trpc } from '../../lib/trpc';
import { useCurrency } from '../../hooks/useCurrency';
import type { CurrencyCode } from '../../lib/currency';

interface PlanSelectorProps {
  currentPlan: 'FREE' | 'PRO' | 'ENTERPRISE';
  onUpgrade: (interval: 'monthly' | 'yearly', currency: CurrencyCode) => void;
  isLoading?: boolean;
}

/**
 * Plan Selector Component
 * Displays FREE and PRO plan cards with monthly/yearly toggle
 */
export function PlanSelector({ currentPlan, onUpgrade, isLoading = false }: PlanSelectorProps) {
  const [isYearly, setIsYearly] = useState(false);
  const { currency, formatPrice, getFormattedPrices } = useCurrency();

  // Fetch available plans
  const { data: plans, isLoading: isLoadingPlans } = trpc.billing.getPlans.useQuery();

  // Get regional prices for Pro plan
  const proPrices = getFormattedPrices('PRO');

  if (isLoadingPlans) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!plans) {
    return null;
  }

  const freePlan = plans.find(p => p.id === 'FREE');
  const proPlan = plans.find(p => p.id === 'PRO');

  if (!freePlan || !proPlan) {
    return null;
  }

  const yearlyDiscount = Math.round(
    ((proPlan.pricing.monthly * 12 - proPlan.pricing.yearly) / (proPlan.pricing.monthly * 12)) * 100
  );

  return (
    <Box>
      {/* Billing Interval Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
        <Typography
          variant="body1"
          sx={{
            mr: 1,
            fontWeight: !isYearly ? 600 : 400,
            color: !isYearly ? 'text.primary' : 'text.secondary',
          }}
        >
          Monthly
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isYearly}
              onChange={(e) => setIsYearly(e.target.checked)}
              color="primary"
            />
          }
          label=""
          sx={{ mx: 0 }}
        />
        <Typography
          variant="body1"
          sx={{
            ml: 1,
            fontWeight: isYearly ? 600 : 400,
            color: isYearly ? 'text.primary' : 'text.secondary',
          }}
        >
          Yearly
        </Typography>
        {isYearly && (
          <Chip
            label={`Save ${yearlyDiscount}%`}
            size="small"
            color="success"
            sx={{ ml: 1, fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Plan Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          maxWidth: 800,
          mx: 'auto',
        }}
      >
        {/* Free Plan Card */}
        <Card
          sx={{
            position: 'relative',
            border: currentPlan === 'FREE' ? '2px solid' : '1px solid',
            borderColor: currentPlan === 'FREE' ? 'primary.main' : 'divider',
          }}
        >
          {currentPlan === 'FREE' && (
            <Chip
              label="Current Plan"
              color="primary"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontWeight: 600,
              }}
            />
          )}
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {freePlan.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {freePlan.description}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h3" component="span" sx={{ fontWeight: 700 }}>
                {formatPrice(0)}
              </Typography>
              <Typography variant="body1" component="span" color="text.secondary">
                /forever
              </Typography>
            </Box>

            <List dense disablePadding sx={{ mb: 3 }}>
              {freePlan.features.map((feature, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>

            {currentPlan === 'FREE' ? (
              <Button
                variant="outlined"
                fullWidth
                disabled
              >
                Current Plan
              </Button>
            ) : (
              <Button
                variant="outlined"
                fullWidth
                disabled
              >
                Downgrade at Period End
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Pro Plan Card */}
        <Card
          sx={{
            position: 'relative',
            border: currentPlan === 'PRO' ? '2px solid' : '2px solid',
            borderColor: currentPlan === 'PRO' ? 'primary.main' : 'secondary.main',
            backgroundColor: currentPlan !== 'PRO' ? 'rgba(16, 185, 129, 0.04)' : undefined,
          }}
        >
          {currentPlan === 'PRO' ? (
            <Chip
              label="Current Plan"
              color="primary"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontWeight: 600,
              }}
            />
          ) : (
            <Chip
              icon={<WorkspacePremiumIcon />}
              label="Recommended"
              color="secondary"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontWeight: 600,
              }}
            />
          )}
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {proPlan.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {proPlan.description}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h3" component="span" sx={{ fontWeight: 700 }}>
                {isYearly ? proPrices.yearlyPerMonth : proPrices.monthly}
              </Typography>
              <Typography variant="body1" component="span" color="text.secondary">
                /month
              </Typography>
              {isYearly && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {proPrices.yearly} billed yearly
                </Typography>
              )}
            </Box>

            <List dense disablePadding sx={{ mb: 3 }}>
              {proPlan.features.map((feature, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>

            {currentPlan === 'PRO' ? (
              <Button
                variant="outlined"
                fullWidth
                disabled
              >
                Current Plan
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={() => onUpgrade(isYearly ? 'yearly' : 'monthly', currency)}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <WorkspacePremiumIcon />}
              >
                {isLoading ? 'Processing...' : 'Upgrade to Pro'}
              </Button>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default PlanSelector;
